/**
 * Hand-tuned library patches (merged on top of fallback + legacy).
 * Moss Matrix routine language only. Not medical advice.
 */

/** @type {Record<string, Record<string, unknown>>} */
export const RICH_LIBRARY_BY_ID = {
  "dandelion-root": {
    glance: {
      title: "Dandelion root at a glance",
      bullets: [
        "Earthy roasted root from the dandelion plant, often dried or roasted for tea-like drinks",
        "Popular in herbal tea traditions and bitter-root kitchen habits",
        "Often chosen for cleansing-style language online; we describe taste and tradition only here",
        "Pairs well with ginger, cinnamon, burdock root, turmeric, and gold sea moss",
        "May suit people who like deep, roasted botanicals instead of sweet fruit gel",
      ],
    },
    traditionalParagraphs: [
      "Dandelion root is the underground part of the common dandelion plant. It is often dried or roasted, then steeped for a deep, tea-like cup that reads more bitter and toasty than sweet.",
      "In herbal kitchens it has long been used as a bitter, roasted-root ingredient alongside other everyday botanicals. Today people still reach for it in digestion-forward, liver-conscious, and fluid-balance routines phrased in traditional language. Moss Matrix does not present it as medical care.",
    ],
    whyPeopleChooseIt: {
      routine:
        "Often chosen by people who already drink roasted-root teas and want that same mood in a spoonful of gel, phrased around digestion or liver-conscious kitchen habits rather than treatment.",
      taste: "Roasted, earthy, slightly bitter, tea-like depth on the spoon.",
      pairing: "Works well with warming roots and spices such as burdock root, ginger, cinnamon, turmeric, and gold sea moss.",
    },
    nutrientsOrCompounds: [
      "The root contains plant fibre including inulin-style compounds that are often discussed in nutrition writing as prebiotic interest alongside other plant matter.",
      "Bitter compounds and polyphenols are part of the plant chemistry people read about in general herb literature. A small powder portion in gel is not the same as a clinical extract dose.",
      "This ingredient is usually chosen as much for traditional use and taste as for any single nutrient headline.",
    ],
    detoxFraming: {
      show: true,
      title: "Does dandelion root support detox?",
      paragraphs: [
        "People often associate dandelion root with cleansing or detox-style routines, especially in traditional language around digestion, bile flow, liver-conscious habits, and fluid balance. Your body already has its own clearance systems. Moss Matrix presents dandelion as a bitter roasted botanical for flavour and routine variety, not as a detox treatment.",
        "Parasite-cleanse conversations sometimes mention dandelion online. Moss Matrix does not present parasite cleansing as an evidence-based outcome of sea moss gel. If you have symptoms or concerns, speak with a qualified healthcare professional.",
        "Nothing here replaces medical advice.",
      ],
    },
    tasteInGel: [
      "In sea moss gel you mostly notice roasted, earthy, slightly bitter, tea-like depth. It can make a blend feel more grounded and less sweet, which many people like in gold-base jars with cinnamon or ginger.",
      "Start with a modest amount if you are new to bitter roots, then adjust in the builder. Sweeten or dilute at home in your own cup if you like.",
    ],
    pairings: [
      { label: "Dandelion root + ginger", reason: "Earthy warmth with a sharper, zesty finish on the spoon." },
      { label: "Dandelion root + burdock root", reason: "Classic root-style herbal blend for people who like deep pantry notes." },
      { label: "Dandelion root + cinnamon", reason: "Softer, warmer spoonful that still reads herbal rather than dessert-like." },
      { label: "Dandelion root + turmeric", reason: "Golden, earthy blend without leaning on fruit in the jar." },
      { label: "Dandelion root + gold sea moss", reason: "Keeps the marine base mellow while the root adds roasted depth." },
    ],
    cautionBullets: [
      "Allergic to ragweed, daisies, marigolds, chrysanthemums, or similar Asteraceae plants",
      "Pregnant or breastfeeding",
      "Taking diuretics (water tablets), lithium, blood thinners, diabetes medicines, blood pressure medicines, or antibiotics",
      "Gallbladder issues, bile duct issues, kidney issues, or other long-term conditions",
      "Unsure whether it fits your medicines or routine",
      "Food amounts in gel are not the same as concentrated herbal supplements",
    ],
    faqs: [
      {
        q: "Can I add dandelion root to sea moss gel?",
        a: "Yes, when Moss Matrix lists it in the builder it ships as a powdered botanical in custom jars. It is for food-style routines, not to treat illness. See our builder and shop pages.",
      },
      {
        q: "What does dandelion root taste like?",
        a: "Roasted, earthy, and slightly bitter, a bit like strong kitchen tea on the spoon. Strength depends on how much you add.",
      },
      {
        q: "Is dandelion root a detox herb?",
        a: "We do not sell it as a detox or parasite cleanse. Some herbal language online uses those words. We focus on taste, tradition, and honest cautions.",
      },
      {
        q: "What pairs well with dandelion root?",
        a: "Ginger, burdock root, cinnamon, turmeric, and gold sea moss are common pairings in customer routines.",
      },
      {
        q: "Who should be careful with dandelion root?",
        a: "Read the cautions list. If you are pregnant, breastfeeding, on medication, managing a condition, buying for a child, or unsure, speak with a healthcare professional.",
      },
    ],
    seoSummary:
      "Learn how dandelion root tastes in sea moss gel, why it appears in herbal routines, what it pairs with, and who should be cautious. Moss Matrix education only.",
    ctaLinks: [
      { label: "Add dandelion root to your blend", href: "/#builder" },
      { label: "Explore root botanicals", href: "/ingredients" },
      { label: "How to use sea moss gel", href: "/learn/how-to-use-sea-moss-gel" },
      { label: "FAQ", href: "/faq" },
    ],
  },

  ginger: {
    glance: {
      title: "Ginger at a glance",
      bullets: [
        "Warm, zesty, slightly sweet heat",
        "Popular in herbal kitchens and everyday tea habits",
        "Often chosen for bright morning-style routines beside sea moss",
        "Pairs with citrus notes, chai-style spices, and gold or purple bases",
      ],
    },
    nutrientsOrCompounds: [
      "Ginger contains volatile oils and pungent compounds that give the familiar aroma. A builder spoonful is for flavour first, not a therapeutic dose.",
      "This ingredient is usually chosen more for its traditional use and taste profile than as a major source of a single nutrient.",
    ],
    pairings: [
      { label: "Ginger + lemon balm", reason: "Bright herbal lift without heavy sweetness." },
      { label: "Ginger + cinnamon", reason: "Cosy kitchen-spice feel on the spoon." },
      { label: "Ginger + turmeric", reason: "Golden warmth with extra zing." },
    ],
    cautionBullets: [
      "May cause stomach upset or heartburn in sensitive people",
      "Blood thinners and surgery: speak with a clinician about concentrated culinary herbs",
      "Pregnancy, breastfeeding, medication, or unsure: ask a healthcare professional",
    ],
  },

  turmeric: {
    nutrientsOrCompounds: [
      "Turmeric rhizome contains curcuminoids that are widely studied in academic literature. Gel uses a food-style powder amount, not a supplement dose.",
      "Often chosen for colour and earthy warmth as much as for chemistry headlines.",
    ],
    pairings: [
      { label: "Turmeric + black pepper", reason: "Classic kitchen pairing people already know from golden drinks." },
      { label: "Turmeric + ginger", reason: "Warm colour with brighter spice lift." },
      { label: "Turmeric + cinnamon", reason: "Softer golden spoonful for morning oats routines at home." },
    ],
    cautionBullets: [
      "May irritate sensitive stomachs",
      "Blood thinners and gallbladder concerns: speak with a healthcare professional",
      "Pregnancy, breastfeeding, or unsure: ask a clinician before regular concentrated use",
    ],
  },

  bladderwrack: {
    nutrientsOrCompounds: [
      "Bladderwrack is a brown seaweed that naturally contains iodine and other sea minerals. Amounts in a gel spoonful vary and are not a replacement for prescribed thyroid care.",
      "Often chosen for briny depth beside sea moss rather than as an iodine supplement.",
    ],
    detoxFraming: {
      show: true,
      title: "Does bladderwrack support detox?",
      paragraphs: [
        "Some online routines talk about sea plants and detox language. Moss Matrix sells bladderwrack as a traditional sea botanical for flavour and pantry variety in food-style gels, not as a detox treatment.",
        "Iodine-rich plants are not right for everyone. If you are pregnant, on thyroid medication, or unsure about iodine, speak with a healthcare professional.",
        "Not medical advice.",
      ],
    },
    pairings: [
      { label: "Bladderwrack + gold sea moss", reason: "Marine-on-marine depth for people who like briny notes.", caution: "Iodine awareness." },
      { label: "Bladderwrack + moringa", reason: "Green briny contrast for adventurous spoonfuls.", caution: "Iodine plus strong greens may not suit everyone." },
    ],
    cautionBullets: [
      "Contains iodine: thyroid conditions, pregnancy, breastfeeding, or thyroid medication need professional advice",
      "Seafood or iodine allergy",
      "Blood thinners or surgery: ask a clinician",
    ],
  },

  "burdock-root": {
    pairings: [
      { label: "Burdock + dandelion root", reason: "Earthy root-cellar blend for bitter-root fans." },
      { label: "Burdock + ginger", reason: "Warming lift against deep earthy base." },
    ],
    nutrientsOrCompounds: [
      "Burdock root is often discussed for inulin-type fibre in herb literature. In gel it is mainly a taste and texture story in small powder amounts.",
    ],
  },

  nettle: {
    pairings: [
      { label: "Nettle + moringa", reason: "Green-on-green mineral-forward spoonful." },
      { label: "Nettle + spirulina", reason: "Bold pond-adjacent note for people who like strong greens." },
    ],
    cautionBullets: [
      "Diuretics, blood pressure medicines, blood thinners, diabetes medicines: speak with a healthcare professional",
      "Pregnancy: nettle leaf traditions vary; ask a midwife or clinician",
      "Kidney fluid balance concerns: seek advice",
    ],
  },

  ashwagandha: {
    cautionBullets: [
      "Sedative medicines or sleep aids: possible interaction concerns, ask a clinician",
      "Thyroid hormone medicines: seek professional advice",
      "Pregnancy: often avoided in concentrated herbal use unless a professional says otherwise",
      "Autoimmune medicines or serious conditions: speak with your specialist",
    ],
    pairings: [
      { label: "Ashwagandha + cinnamon", reason: "Soft warming frame around earthy root powder." },
      { label: "Ashwagandha + reishi", reason: "Evening-style calm routine pairing for people who like quiet botanical blends." },
    ],
  },

  "matcha-green-tea": {
    cautionBullets: [
      "Contains caffeine: sensitive users should start small",
      "Pregnancy, breastfeeding, heart rhythm concerns, or stimulant-sensitive medicines: ask a clinician",
      "Children: no dosage advice here, speak with a professional",
    ],
    nutrientsOrCompounds: [
      "Matcha contains caffeine and chlorophyll-rich green tea solids. It is a flavour and colour ingredient in gel, not an energy product claim.",
    ],
  },

  "green-tea-extract": {
    cautionBullets: [
      "Contains caffeine from tea solids",
      "Blood thinners or stimulant-sensitive medicines: speak with a healthcare professional",
      "Pregnancy and breastfeeding: ask a clinician",
    ],
  },

  spirulina: {
    nutrientsOrCompounds: [
      "Spirulina is a cyanobacteria powder often discussed for protein and pigment content. Gel amounts are small and chosen for colour and taste as much as any single nutrient.",
    ],
    pairings: [
      { label: "Spirulina + lime zest at home", reason: "Bright contrast after you stir gel into your own drink." },
      { label: "Spirulina + moringa", reason: "Deep green stack for people who already like pond-forward powders." },
    ],
  },

  chlorella: {
    nutrientsOrCompounds: [
      "Chlorella is a green micro-algae powder with a strong pond-like note. Often chosen for colour-forward green routines, not as a heavy metal treatment.",
    ],
  },

  elderberry: {
    nutrientsOrCompounds: [
      "Elderberry is used as a prepared botanical powder in our gels. Traditional syrups exist in kitchens; our jar is a different format and still not a treatment for illness.",
    ],
    cautionBullets: [
      "Raw elder plant parts are not safe to eat; we use prepared powder only",
      "Autoimmune medicines or transplants: speak with a specialist before concentrated botanicals",
      "Pregnancy, breastfeeding, or unsure: ask a clinician",
    ],
  },

  hibiscus: {
    tasteInGel: [
      "Tart, cranberry-like, floral lift. It can steer a gel toward a fruit-tea mood without fresh fruit in the jar.",
      "Pairs beautifully with sweeter bases at home when you stir gel into juice you choose yourself.",
    ],
    cautionBullets: [
      "May affect blood pressure in some people",
      "Pregnancy: hibiscus is often avoided in concentrated herbal use unless a professional says otherwise",
      "Medication for blood pressure or kidneys: ask a clinician",
    ],
  },

  moringa: {
    pairings: [
      { label: "Moringa + spirulina", reason: "Deep green mineral-forward routine." },
      { label: "Moringa + ginger", reason: "Bright lift against grassy depth." },
    ],
  },

  "milk-thistle": {
    detoxFraming: {
      show: true,
      title: "Does milk thistle support detox?",
      paragraphs: [
        "Milk thistle is often discussed online next to liver and detox language. Moss Matrix lists it as a traditional botanical powder for pantry-style routines, not as a liver treatment or cleanse.",
        "Not medical advice. Speak with a healthcare professional if you manage liver conditions or take medicines processed by the liver.",
      ],
    },
    cautionBullets: [
      "Allergy to plants in the daisy family",
      "Hormone-sensitive conditions or medicines: seek professional advice",
      "Liver disease or liver medicines: speak with your clinician before concentrated herbs",
    ],
  },

  "reishi-mushroom": {
    cautionBullets: [
      "Blood thinners or surgery scheduling: speak with a clinician",
      "Immune-modulating medicines: ask your specialist",
      "Pregnancy and breastfeeding: seek professional advice",
    ],
    pairings: [{ label: "Reishi + cacao", reason: "Quiet evening cocoa mood without claiming sleep outcomes." }],
  },

  cinnamon: {
    pairings: [
      { label: "Cinnamon + cacao", reason: "Bakery-adjacent warmth." },
      { label: "Cinnamon + ginger", reason: "Chai-style lift in gel." },
    ],
  },

  cacao: {
    cautionBullets: [
      "Contains small amounts of caffeine and theobromine",
      "Migraine triggers or stimulant sensitivity: start small",
      "MAOI medicines: speak with a clinician before concentrated cacao botanicals",
    ],
  },

  cayenne: {
    cautionBullets: [
      "Hot spice: digestive or skin sensitivity possible",
      "Blood thinners, NSAIDs, or stomach ulcers: speak with a healthcare professional",
      "Touch eyes after handling spicy powders with care",
    ],
  },

  clove: {
    cautionBullets: [
      "Very potent spice: tiny amounts in gel",
      "Blood thinners: speak with a clinician about concentrated culinary spices",
    ],
  },

  thyme: {
    cautionBullets: [
      "Concentrated thyme botanicals may not suit everyone with hormone-sensitive conditions; ask a clinician if unsure",
      "Pregnancy in high concentrated herbal contexts: seek advice",
    ],
  },

  garlic: {
    traditionalParagraphs: [
      "Garlic is a savoury allium from kitchens worldwide. Moss Matrix keeps garlic in the ingredient library for education. It is not in the default sweet herb builder.",
    ],
    cautionBullets: [
      "May irritate sensitive stomachs",
      "Bleeding risk or anticoagulants: speak with a healthcare professional",
      "Not a fit for sweet dessert-style gels",
    ],
  },

  "lion-mane-mushroom": {
    cautionBullets: [
      "Mushroom allergy",
      "Blood thinners or surgery: ask a clinician",
      "Asthma or immune-modulating medicines: seek professional advice",
    ],
  },

  "cordyceps-mushroom": {
    cautionBullets: [
      "Autoimmune conditions or immune medicines: speak with a specialist",
      "Bleeding disorders or anticoagulants: ask a clinician",
      "Pregnancy and breastfeeding: seek advice",
    ],
  },

  maca: {
    cautionBullets: [
      "Hormone-sensitive conditions: speak with a clinician before regular concentrated use",
      "Thyroid medicines: seek advice (iodine content varies by product)",
    ],
  },

  rosehip: {
    tasteInGel: [
      "Tart, fruity, vitamin-C-adjacent kitchen note. Brightens gel toward cordial moods without fresh fruit shipped in the jar.",
    ],
  },

  baobab: {
    tasteInGel: [
      "Tart, sherbet-like citrus lift. Popular with people who like zing on the spoon.",
    ],
  },

  "acerola-cherry": {
    nutrientsOrCompounds: [
      "Acerola is often associated with vitamin C in fruit literature. Powder in gel is a tart flavour note, not a supplement replacement.",
    ],
  },
};

/** @type {Record<string, { seoTitle: string, seoDescription: string }>} */
export const RICH_SEO_BY_ID = {
  "dandelion-root": {
    seoTitle: "Dandelion root in sea moss gel | taste, tradition and pairings | Moss Matrix",
    seoDescription:
      "Learn how dandelion root tastes in Moss Matrix sea moss gel, why it appears in herbal routines, what it pairs with, and who should be cautious. Food education only.",
  },
  ginger: {
    seoTitle: "Ginger in sea moss gel | Moss Matrix ingredient library",
    seoDescription: "Ginger taste in Moss Matrix custom gels, traditional kitchen use, pairings, and cautions. Not medical advice.",
  },
  turmeric: {
    seoTitle: "Turmeric in sea moss gel | Moss Matrix ingredient library",
    seoDescription: "Turmeric in Moss Matrix gels: golden flavour, pairings, and honest cautions. Routine education only.",
  },
  bladderwrack: {
    seoTitle: "Bladderwrack in sea moss gel | Moss Matrix",
    seoDescription: "Bladderwrack taste, iodine cautions, and pairings with Moss Matrix sea moss gel. Not medical advice.",
  },
};
